import { Model } from '@vuex-orm/core'
import { Request } from '@vuex-orm/plugin-axios'
import AppState from '@/utils/app.state'
import { getStringFromArray } from '@/lang'
import type { LangedItem } from '../common/commons'

interface OptionalPropertyPolicy {
  dataProtection: LangedItem[]
  otherPolicies: LangedItem[]
  termsAndConditions: LangedItem[]
}

export default class Hotel extends Model {
  declare id: number
  declare address1: string
  declare clientId: number
  declare name: string
  declare descriptions: LangedItem[]
  declare propertyRating: object
  declare checkInTime: string
  declare checkOutTime: string
  declare propertyType: number
  declare city: LangedItem[]
  declare postcode: string
  declare countryCode: string
  declare country: string
  declare timeZone: string
  declare propertyCurrency: string
  declare state: string
  declare images: { url: string, isFloorPlan: boolean }[]
  declare optionalPropertyPolicies?: OptionalPropertyPolicy

  static entity = 'hotels'
  static primaryKey = 'id'

  static fields() {
    return {
      id: this.attr(null),
      address1: this.attr(null),
      clientId: this.attr(null),
      name: this.string(''),
      descriptions: this.attr(null),
      propertyRating: this.attr({}),
      checkInTime: this.attr(null),
      checkOutTime: this.attr(null),
      propertyType: this.attr(null),
      city: this.attr(null),
      postcode: this.attr(null),
      countryCode: this.attr(null),
      country: this.attr(null),
      timeZone: this.attr(null),
      propertyCurrency: this.attr(null),
      state: this.attr(null),
      images: this.attr({}),
      optionalPropertyPolicies: this.attr(null).nullable()
    }
  }

  get langedCity() {
    return this.city && this.city[0] && this.city[0].value
  }

  get image() {
    return this.images && this.images[0] && this.images[0].url
  }

  static getAdditionalCharges() {
    const hotel = this.query().first()
    if (
      !hotel ||
      !hotel.optionalPropertyPolicies ||
      !Array.isArray(hotel.optionalPropertyPolicies.otherPolicies) ||
      !hotel.optionalPropertyPolicies.otherPolicies.every(e => e.languageCode)
    ) { return }

    return getStringFromArray(
      hotel.optionalPropertyPolicies.otherPolicies as (LangedItem & Record<string, string>)[],
      false,
      'value'
    )
  }

  static getFloorplans() {
    const hotel = this.query().first()
    if (!hotel || !hotel.images) return []
    return hotel.images.filter(it => it.isFloorPlan)
  }

  static getTerms() {
    const hotel = this.query().first()
    if (
      !hotel ||
      !hotel.optionalPropertyPolicies ||
      !Array.isArray(hotel.optionalPropertyPolicies.termsAndConditions) ||
      !hotel.optionalPropertyPolicies.termsAndConditions.every(e => e.languageCode)
    ) { return }

    return getStringFromArray(
      hotel.optionalPropertyPolicies.termsAndConditions as (LangedItem & Record<string, string>)[],
      false,
      'value'
    )
  }

  static getPrivacyPolicy() {
    const hotel = this.query().first()
    if (
      !hotel ||
      !hotel.optionalPropertyPolicies ||
      !Array.isArray(hotel.optionalPropertyPolicies.dataProtection) ||
      !hotel.optionalPropertyPolicies.dataProtection.every(e => e.languageCode)
    ) { return }

    return getStringFromArray(
      hotel.optionalPropertyPolicies.dataProtection as (LangedItem & Record<string, string>)[],
      false,
      'value'
    )
  }

  static apiConfig = {
    actions: {
      fetch(this: Request) {
        return this.post(`/content/v1/properties/getbypropertyids`, {
          IDs: [AppState.currentProperty],
          getImages: true,
          getAmenities: false,
          getDescriptions: true,
          getRooms: false,
          getOptionalPropertyPolicies: true,
          getRatePlans: false
        })
      }
    }
  }

}

